import { styled } from "@mui/material/styles";
import { Box, Typography, DialogContent, Checkbox } from "@mui/material";
import { colors } from "../../../theme/theme";
import { CustomBtn } from "../../Common/CustomBtn";

export const StyledDialogTitle = styled(Box)(({ theme }) => ({
  paddingBottom: theme.spacing(2),
  paddingTop: theme.spacing(3),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  backgroundColor: colors.headerBg,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  [theme.breakpoints.down("sm")]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

export const StyledTitleText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 700,
  color: colors.primary,
  fontSize: "1.25rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

export const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingBottom: theme.spacing(2),
  backgroundColor: "#FAFAFA",
  [theme.breakpoints.down("sm")]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

export const AgreementScrollBox = styled(Box)(({ theme }) => ({
  maxHeight: "400px",
  overflowY: "auto",
  border: `1px solid ${colors.primary}20`,
  borderRadius: "12px",
  backgroundColor: "#FFFFFF",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: colors.primary + "40",
    borderRadius: "3px",
  },
  [theme.breakpoints.down("sm")]: {
    maxHeight: "300px",
    padding: theme.spacing(2),
  },
}));

export const AgreementSectionHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 700,
  color: colors.primary,
  fontSize: "1rem",
  marginTop: theme.spacing(2.5),
  marginBottom: theme.spacing(1),
  "&:first-of-type": {
    marginTop: 0,
  },
}));

export const AgreementSectionSubtitle = styled(Typography)({
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 600,
  color: colors.primary,
  fontSize: "0.9rem",
  marginBottom: 8,
});

export const AgreementText = styled(Typography)({
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 400,
  color: colors.text,
  fontSize: "0.875rem",
  lineHeight: 1.7,
  marginBottom: 8,
});

export const AgreementListItem = styled("li")({
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 400,
  color: colors.text,
  fontSize: "0.875rem",
  lineHeight: 1.7,
  marginBottom: 4,
});

export const SignatureSection = styled(Box)(({ theme }) => ({
  border: `2px solid ${colors.primary}30`,
  borderRadius: "14px",
  backgroundColor: `${colors.primary}05`,
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

export const SignatureSectionTitle = styled(Typography)({
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 700,
  color: colors.primary,
  fontSize: "1rem",
  marginBottom: 16,
});

export const StyledCheckbox = styled(Checkbox)({
  color: colors.primary,
  "&.Mui-checked": {
    color: colors.secondary,
  },
});

export const CheckboxLabel = styled(Typography)({
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 500,
  color: colors.primary,
  fontSize: "0.875rem",
  lineHeight: 1.5,
});

export const DateDisplay = styled(Typography)(({ theme }) => ({
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 500,
  color: colors.textLight,
  fontSize: "0.85rem",
  marginTop: theme.spacing(1.5),
}));

export const ActionBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(2),
  paddingTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column-reverse",
  },
}));

export const SignButton = styled(CustomBtn)(({ theme }) => ({
  backgroundColor: colors.secondary,
  "&:hover": {
    backgroundColor: "#1e3047",
  },
  "&.Mui-disabled": {
    backgroundColor: "#ccc",
    color: "#999",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

export const CancelButton = styled(CustomBtn)(({ theme }) => ({
  backgroundColor: "transparent",
  color: colors.primary,
  border: `1px solid ${colors.primary}40`,
  "&:hover": {
    backgroundColor: `${colors.primary}08`,
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

export const SuccessBanner = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(76, 175, 80, 0.08)",
  border: "1px solid rgba(76, 175, 80, 0.3)",
  borderRadius: "12px",
  padding: theme.spacing(2),
  textAlign: "center",
  marginBottom: theme.spacing(2),
}));

export const SuccessText = styled(Typography)({
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 600,
  color: "#2E7D32",
  fontSize: "0.95rem",
});

// --- Parent / Legal Guardian section (for minor clients 14-17) ---

export const GuardianSection = styled(Box)(({ theme }) => ({
  border: "2px solid rgba(211, 47, 47, 0.25)",
  borderRadius: "14px",
  backgroundColor: "rgba(211, 47, 47, 0.03)",
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

export const GuardianSectionTitle = styled(Typography)({
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 700,
  color: "#B71C1C",
  fontSize: "1rem",
  marginBottom: 8,
});

export const GuardianNotice = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(211, 47, 47, 0.06)",
  border: "1px solid rgba(211, 47, 47, 0.2)",
  borderRadius: "10px",
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(2.5),
}));

export const GuardianNoticeText = styled(Typography)({
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 500,
  color: "#B71C1C",
  fontSize: "0.85rem",
  lineHeight: 1.6,
});
